import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from '../utils/safeToast';
import { Plus, Edit, Trash2, Loader2, Search } from 'lucide-react';

const API = "/api";

const OwnersPage = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    salutation: '',
    first_name: '',
    name: '',
    email: '',
    phone: '',
    address: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOwners();
  }, []);

  const fetchOwners = async () => {
    try {
      const response = await axios.get(`${API}/owners`);
      setOwners(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Eigentümer');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/owners`, formData);
      toast.success('Eigentümer erfolgreich erstellt');
      setDialogOpen(false);
      setFormData({ salutation: '', first_name: '', name: '', email: '', phone: '', address: '' });
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedOwner) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/owners/${selectedOwner.id}`, formData);
      toast.success('Eigentümer aktualisiert');
      setEditDialogOpen(false);
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOwner) return;
    try {
      await axios.delete(`${API}/owners/${selectedOwner.id}`);
      toast.success('Eigentümer gelöscht');
      setDeleteDialogOpen(false);
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEditDialog = (owner) => {
    setSelectedOwner(owner);
    setFormData({
      salutation: owner.salutation || '',
      first_name: owner.first_name || '',
      name: owner.name,
      email: owner.email,
      phone: owner.phone || '',
      address: owner.address || '',
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (owner) => {
    setSelectedOwner(owner);
    setDeleteDialogOpen(true);
  };

  const filteredOwners = owners.filter(owner =>
    owner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    owner.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="owners-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Eigentümer</h1>
          <p className="text-stone-500 text-sm">{owners.length} Eigentümer verwalten</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-full" data-testid="add-owner-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neuer Eigentümer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Neuen Eigentümer anlegen</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="salutation">Anrede</Label>
                  <Select
                    value={formData.salutation || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, salutation: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger className="bg-stone-50" data-testid="owner-salutation-select">
                      <SelectValue placeholder="Keine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Keine</SelectItem>
                      <SelectItem value="Herr">Herr</SelectItem>
                      <SelectItem value="Frau">Frau</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="first_name">Vorname</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Max"
                    className="bg-stone-50"
                    data-testid="owner-first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Mustermann"
                    required
                    className="bg-stone-50"
                    data-testid="owner-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="max@example.de"
                  required
                  className="bg-stone-50"
                  data-testid="owner-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                  className="bg-stone-50"
                  data-testid="owner-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Musterstraße 1, 12345 Stadt"
                  className="bg-stone-50"
                  data-testid="owner-address-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full">
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                  disabled={submitting}
                  data-testid="owner-submit-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Erstellen
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <Input
          placeholder="Eigentümer suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-stone-200"
          data-testid="owner-search"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-stone-50">
              <TableRow>
                <TableHead className="font-semibold text-slate-900">Anrede</TableHead>
                <TableHead className="font-semibold text-slate-900">Vorname</TableHead>
                <TableHead className="font-semibold text-slate-900">Name</TableHead>
                <TableHead className="font-semibold text-slate-900">E-Mail</TableHead>
                <TableHead className="font-semibold text-slate-900">Telefon</TableHead>
                <TableHead className="font-semibold text-slate-900">Adresse</TableHead>
                <TableHead className="font-semibold text-slate-900 text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOwners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-stone-500">
                    {searchTerm ? 'Keine Eigentümer gefunden' : 'Noch keine Eigentümer angelegt'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredOwners.map((owner) => (
                  <TableRow
                    key={owner.id}
                    className="hover:bg-stone-50 transition-colors"
                    data-testid={`owner-row-${owner.id}`}
                  >
                    <TableCell className="text-stone-600">{owner.salutation || '–'}</TableCell>
                    <TableCell className="text-stone-600">{owner.first_name || '–'}</TableCell>
                    <TableCell className="font-medium text-slate-900">{owner.name}</TableCell>
                    <TableCell>
                      <a href={`mailto:${owner.email}`} className="text-[#0E4C92] hover:underline">
                        {owner.email}
                      </a>
                    </TableCell>
                    <TableCell className="text-stone-600">{owner.phone || '–'}</TableCell>
                    <TableCell className="text-stone-600 max-w-[200px] truncate">{owner.address || '–'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(owner)}
                          data-testid={`edit-owner-${owner.id}`}
                          className="text-[#0E4C92] border-[#0E4C92] hover:bg-[#0E4C92] hover:text-white"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Bearbeiten
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteDialog(owner)}
                          className="text-red-600 border-red-300 hover:bg-red-600 hover:text-white hover:border-red-600"
                          data-testid={`delete-owner-${owner.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Löschen
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Eigentümer bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-salutation">Anrede</Label>
                <Select
                  value={formData.salutation || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, salutation: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-stone-50" data-testid="edit-owner-salutation">
                    <SelectValue placeholder="Keine" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine</SelectItem>
                    <SelectItem value="Herr">Herr</SelectItem>
                    <SelectItem value="Frau">Frau</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-first-name">Vorname</Label>
                <Input
                  id="edit-first-name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="bg-stone-50"
                  data-testid="edit-owner-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-stone-50"
                  data-testid="edit-owner-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">E-Mail</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-stone-50"
                data-testid="edit-owner-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Telefon</Label>
              <Input
                id="edit-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-stone-50"
                data-testid="edit-owner-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Adresse</Label>
              <Input
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-stone-50"
                data-testid="edit-owner-address"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1 rounded-full">
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                disabled={submitting}
                data-testid="update-owner-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eigentümer löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie &quot;{selectedOwner?.name}&quot; wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 rounded-full"
              data-testid="confirm-delete-owner"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OwnersPage;
